import React, { useCallback, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ReportParameterListCard from './ReportParameterListCard';
import { initialState, reducer } from '../../../reducers/reportParameters/containerReducer';
import { useToast } from 'hooks/toasts/useToast';
import { useValidation } from '@totalsoft/pure-validations-react';
import { reportParameterValidator } from 'features/reports/validators';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { REPORT_PARAMETER_TYPES_QUERY, REPORT_CONTEXT_PARAMETERS_QUERY } from '../queries';
import { ADD_UPDATE_REPORT_PARAMETER, REMOVE_REPORT_PARAMETER_INPUT } from '../mutations';
import { REPORT_PARAMETER_UPDATED, REPORT_PARAMETER_REMOVED } from '../subscriptions';

import useLanguageTracker from 'hooks/useLanguageTracker';

const ReportParameterContainer = ({ data, reportId }) => {
  const addToast = useToast();
  const [t] = useTranslation();

  const [localState, dispatch] = useReducer(reducer, initialState);
  const [reportParametersValidation, validateReportParameters] = useValidation(reportParameterValidator);

  // #region Queries

  const { data: reportParameterTypesData, refetch: refetchReportParameterTypes } = useQuery(REPORT_PARAMETER_TYPES_QUERY);

  const { data: reportContextParametersData, refetch: refetchReportContextParameters } = useQuery(REPORT_CONTEXT_PARAMETERS_QUERY);

  // #endregion

  // #region Mutations

  const [addOrUpdateReportParameter] = useMutation(ADD_UPDATE_REPORT_PARAMETER);

  const [removeReportParameter] = useMutation(REMOVE_REPORT_PARAMETER_INPUT);

  // #endregion

  // #region Subscriptions

  useSubscription(REPORT_PARAMETER_UPDATED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('Reports.ReportParameterUpdated'), 'success');
      client.cache.evict({
        id: `ReportItem:{"reportId":${data.reportParameterUpdated.reportParameter.reportId}}`
      });
    }
  });

  useSubscription(REPORT_PARAMETER_REMOVED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('Reports.ReportParameterRemoved'), 'success');
      client.cache.evict({
        id: `ReportParameterItem:{"reportParameterId":${data.reportParameterRemoved.reportParameterId}}`
      });
    }
  });

  // #endregion

  // #region Side effects

  const onLanguageChange = useCallback(() => {
    refetchReportParameterTypes();
    refetchReportContextParameters();
  }, [refetchReportParameterTypes, refetchReportContextParameters]);

  useLanguageTracker({
    onChange: onLanguageChange
  });

  useEffect(() => {
    if (data) dispatch({ type: 'OnInitState', data });
  }, [data]);

  useEffect(() => {
    validateReportParameters(localState.values);
  }, [localState.values, validateReportParameters]);

  // #endregion

  // #region Handlers

  const handleAdd = () => {
    dispatch({
      type: 'OnReportParameterAdded',
      reportId
    });
  };

  const handleExpandedChanged = (isExpanded, index) => {
    dispatch({ type: 'OnReportParameterExpanded', index, isExpanded });
  };

  const handleDelete = (reportParameter, index) => () => {
    if (window.confirm(t('Reports.ReportParameters.DeleteReportParameter'))) {
      if (reportParameter.reportParameterId) {
        const input = { reportParameterId: reportParameter.reportParameterId, reportId: reportParameter.reportId };
        removeReportParameter({ variables: { input } });
      }
      dispatch({ type: 'OnReportParameterDeleted', index });
    }
  };

  const handlePropertyChanged = (propertyName, value, index) => {
    dispatch({ type: 'OnReportParameterPropertyChanged', propertyName, value, index });
  };

  const handleSave = index => () => {
    if (!validateReportParameters(localState.values)) return;
    // eslint-disable-next-line no-unused-vars
    const { changed, isExpanded, ...reportParameter } = localState.values[index];
    const input = { ...reportParameter };

    dispatch({ type: 'OnReportParameterSaved', index });
    return addOrUpdateReportParameter({ variables: { input } });
  };

  // #endregion

  return (
    <ReportParameterListCard
      reportParameters={localState.values}
      onAdd={handleAdd}
      onPropertyChanged={handlePropertyChanged}
      onSave={handleSave}
      onDelete={handleDelete}
      onExpandedChanged={handleExpandedChanged}
      reportParameterTypes={reportParameterTypesData ? reportParameterTypesData.reportParameterTypes : []}
      reportContextParameters={reportContextParametersData ? reportContextParametersData.reportContextParameters : []}
      validation={reportParametersValidation}
    />
  );
};

ReportParameterContainer.propTypes = {
  data: PropTypes.array.isRequired,
  reportId: PropTypes.number
};

export default ReportParameterContainer;
